import type { CustomLayerInterface, Map as MapboxMap } from "mapbox-gl";
import mapboxgl from "mapbox-gl";
import {
  Camera,
  DoubleSide,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Scene,
  Vector3,
  WebGLRenderer,
} from "three";
import type { LocalOrigin } from "./geometryUtils";

const LAYER_ID = "simshady-result-mesh";

/** Simshady returns vertex-colored meshes; use unlit material so colors show on the map. */
export function prepareSimshadyResultMesh(mesh: Mesh): Mesh {
  if (Array.isArray(mesh.material)) {
    mesh.material.forEach((material) => material.dispose());
  } else {
    mesh.material.dispose();
  }

  mesh.material = new MeshBasicMaterial({
    vertexColors: true,
    side: DoubleSide,
    transparent: true,
    opacity: 0.92,
    depthWrite: true,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });

  return mesh;
}

/**
 * Mapbox GL custom 3D layer that renders a simshady result mesh with Three.js.
 */
export class SimShadyMeshLayer {
  private map: MapboxMap | null = null;
  private customLayer: CustomLayerInterface | null = null;
  private scene = new Scene();
  private camera = new Camera();
  private renderer: WebGLRenderer | null = null;
  private resultMesh: Mesh | null = null;
  private visible = true;
  private modelTransform = {
    translateX: 0,
    translateY: 0,
    translateZ: 0,
    rotateX: 0,
    rotateY: 0,
    rotateZ: 0,
    scale: 1,
  };

  addTo(map: MapboxMap): this {
    if (this.map === map && this.customLayer) return this;

    this.remove();
    this.map = map;
    this.customLayer = this.createCustomLayer();
    map.addLayer(this.customLayer);
    return this;
  }

  /** Attach the mesh returned by `ShadingScene.calculate()` to this custom layer. */
  setMesh(mesh: Mesh, origin: LocalOrigin): void {
    this.clearMesh();

    prepareSimshadyResultMesh(mesh);
    this.resultMesh = mesh;
    this.updateModelTransform(origin);

    if (this.renderer) {
      this.scene.add(mesh);
    }

    this.map?.triggerRepaint();
  }

  hasMesh(): boolean {
    return this.resultMesh !== null;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    if (this.resultMesh) {
      this.resultMesh.visible = visible;
      this.map?.triggerRepaint();
    }
  }

  remove(): void {
    if (this.map && this.customLayer && this.map.getLayer(LAYER_ID)) {
      this.map.removeLayer(LAYER_ID);
    }

    this.clearMesh();
    this.disposeRenderer();
    this.map = null;
    this.customLayer = null;
  }

  private createCustomLayer(): CustomLayerInterface {
    return {
      id: LAYER_ID,
      type: "custom",
      renderingMode: "3d",
      onAdd: (map, gl) => {
        this.renderer = new WebGLRenderer({
          canvas: map.getCanvas(),
          context: gl,
          antialias: true,
        });
        this.renderer.autoClear = false;

        if (this.resultMesh) {
          this.scene.add(this.resultMesh);
        }
      },
      render: (_gl, matrix) => {
        if (!this.renderer || !this.resultMesh || !this.visible) return;

        const rotationX = new Matrix4().makeRotationAxis(
          new Vector3(1, 0, 0),
          this.modelTransform.rotateX,
        );
        const rotationY = new Matrix4().makeRotationAxis(
          new Vector3(0, 1, 0),
          this.modelTransform.rotateY,
        );
        const rotationZ = new Matrix4().makeRotationAxis(
          new Vector3(0, 0, 1),
          this.modelTransform.rotateZ,
        );

        const projection = new Matrix4().fromArray(matrix);
        const model = new Matrix4()
          .makeTranslation(
            this.modelTransform.translateX,
            this.modelTransform.translateY,
            this.modelTransform.translateZ,
          )
          .scale(
            new Vector3(
              this.modelTransform.scale,
              -this.modelTransform.scale,
              this.modelTransform.scale,
            ),
          )
          .multiply(rotationX)
          .multiply(rotationY)
          .multiply(rotationZ);

        this.resultMesh.visible = this.visible;
        this.camera.projectionMatrix = projection.multiply(model);
        this.renderer.resetState();
        this.renderer.render(this.scene, this.camera);
      },
      onRemove: () => {
        this.disposeRenderer();
      },
    };
  }

  private updateModelTransform(origin: LocalOrigin) {
    const modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
      [origin.lng, origin.lat],
      0,
    );

    this.modelTransform = {
      translateX: modelAsMercatorCoordinate.x,
      translateY: modelAsMercatorCoordinate.y,
      translateZ: modelAsMercatorCoordinate.z,
      rotateX: 0,
      rotateY: 0,
      rotateZ: 0,
      scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits(),
    };
  }

  private clearMesh() {
    if (this.resultMesh) {
      this.scene.remove(this.resultMesh);
      this.resultMesh.geometry.dispose();
      if (Array.isArray(this.resultMesh.material)) {
        this.resultMesh.material.forEach((material) => material.dispose());
      } else {
        this.resultMesh.material.dispose();
      }
      this.resultMesh = null;
    }
  }

  private disposeRenderer() {
    this.renderer?.dispose();
    this.renderer = null;
    this.scene.clear();
  }
}

export default SimShadyMeshLayer;
